import Strategies from "features/bots/BotForm/Strategies/Strategies";
import TrailingEntryAndTakeProfit from "features/bots/BotForm/common/TrailingEntryAndTakeProfit";
import React, { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType } from "@shared/types";
import { SectionTitle } from "features/bots/BotForm/common/styles";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  HedgingMode,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import styled from "styled-components";
import RestoreChangesButton from "features/bots/BotForm/common/RestoreChangesButton";
import { useEntries } from "@shared/features/bots/BotForm/common/hooks";
import { HEDGING_SYMBOL_OVERRIDE_WARNING } from "@shared/features/bots/BotForm/General/useGridsSelector";
import { FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import GridsSelector from "features/bots/BotForm/Strategies/GridsSelector";
import GracePercent from "features/bots/BotForm/common/GracePercent";
import HedgingModeSelector from "features/bots/BotForm/Entries/HedgingModeSelector.tsx";
import { isSymbol } from "@shared/types/utils.ts";
import { openMessage } from "utils/misc.tsx";
import { HEDGING_TAB_OVERRIDE_BUTTON_ID } from "testIds.ts";

const Entries = () => {
  const { botType, hedgingMode, isChannelConfiguration, symbols } =
    useContext(BotFormContext)!;
  const { isToggleField, title, getDefaultValue } = useEntries();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const showEntriesToggle =
    isToggleField && (!isHedgingBot || hedgingMode !== HedgingMode.FIXED);
  const trailingInput =
    isGridBot || isHedgingBot ? undefined : (
      <TrailingEntryAndTakeProfit
        orderType={OrderTypes.ENTRY}
        key="trailingEntry"
      />
    );

  const getEntryGraceInput = () => {
    if (!isSignalsBot || isChannelConfiguration) return;

    return (
      <div style={{ marginTop: 16 }}>
        <TitleWithToggle
          title={t(FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE)}
          content={<GracePercent isEntry={true} />}
          fieldType={ToggleField.ENTRY_GRACE_PERCENT}
        />
      </div>
    );
  };

  const content = (
    <>
      {isHedgingBot && <HedgingModeSelector />}
      {isGridBot && (
        <GridsSelector style={{ marginBottom: 16 }} updateGridsData={true} />
      )}
      {!isHedgingBot && <Strategies orderType={OrderTypes.ENTRY} />}
      {!isSignalsBot && trailingInput}
    </>
  );

  const titleComponent = title ? (
    <SectionTitle>{title}</SectionTitle>
  ) : undefined;

  const getContent = () => {
    if (showEntriesToggle) {
      return (
        <TitleWithToggle
          title={titleComponent}
          content={content}
          fieldType={ToggleField.ENTRIES}
          size="default"
          hideWithOpacity={true}
          getDefaultValue={getDefaultValue}
          overrideObjectName={isHedgingBot ? t("labels.hedging") : undefined}
          hideToggle={isHedgingBot}
          disableOverrideButton={isHedgingBot && isSymbol(symbols)}
          onDisabledOverrideButtonClick={() =>
            openMessage(HEDGING_SYMBOL_OVERRIDE_WARNING, "warning")
          }
          overrideButtonWrapperTestId={HEDGING_TAB_OVERRIDE_BUTTON_ID}
        />
      );
    }
    return content;
  };

  return (
    <Wrapper>
      <RestoreChangesButton
        tab={Steps.ENTRIES}
        title={!showEntriesToggle && titleComponent}
      />
      {getContent()}
      {isSignalsBot && (
        <>
          {trailingInput}
          {getEntryGraceInput()}
        </>
      )}
    </Wrapper>
  );
};

export default React.memo(Entries);

const Wrapper = styled.div`
  position: relative;
`;
